import { useCallback, useState } from 'react'
import { ModalCreateEditMedium } from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { FormElement, FormRender } from '../../../../../../../forms/FormRenderer/types'
import { ImageInputFile } from '../../../../../../../forms/ImageInput/types'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveLangs, getAllSpecificPriceRules, getModulesBuyed } from '../../../../../../../utils/reducers/getters'
import { GroupVatJson, GroupVatPayload } from '../../../../../../../utils/reducers/globals/groupVats'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { getLang, haveModuleBuyed } from '../../../../../../../utils/globals'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { importProductTypesInversed } from '@teinor/erp/types/company/product'
import { FeaturesPayload } from '../../../../../../../utils/reducers/products/features'

type ModalImportProductsProps = ModalEditComponentProps & ReduxModalImportProducts & {}

const ModalImportProducts: React.FC<ModalImportProductsProps> = ({ userPermissions, allRowData, groupVats, features, ...rest }) => {
    const { t } = useTwinTranslation()
    const { fields, createType } = useModalImportProductsLogic({ groupVats, features })

    return (
        <ModalCreateEditMedium url='/api/app/product/importProducts' {...rest} fields={fields} translations={{
            title: t('importProducts', 'Importar productos'),
            button: createType? t('create', 'Crear') : t('update', 'Actualizar')
        }} haveButtonPermissions={true} imagePath={['tmp']} >
        </ModalCreateEditMedium>
    )
}
interface ModalImportProductsLogicProps {
    groupVats: GroupVatJson
    features: FeaturesPayload
}
const useModalImportProductsLogic = ({ groupVats, features }: ModalImportProductsLogicProps ) => {
    const {t} = useTwinTranslation()
    const [file, setFile] = useState <ImageInputFile[]>([])
    const [importProductType, setImportProductType] = useState(importProductTypesInversed['create'])
    
    const pricesRules = getAllSpecificPriceRules()
    const csvFields: FormElement<any>[] = []
    const dictImportTypes: TwinDictionary = {
        0: { name: t('create', 'Crear'), value: 0 },
        1: { name: t('update', 'Actualizar'), value: 1 },
    }

    const handleImportProductType = useCallback((val: string) => {
        setImportProductType(parseInt(val))
    }, [setImportProductType])

    const createType = importProductType === importProductTypesInversed['create']

    if (file.length){
        csvFields.push(
            {
                name: 'delimiter',
                label: t('delimiter', 'Delimitado'),
                component: 'InputWithLabelMargin',
                defaultValue: ';',
                required: true,
            },
            {
                name: 'rowsToSkip',
                label: t('rowsToSkip', 'Filas que saltar'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posRefColumn',
                label: t('posProductRefColumn', 'Posición columna referencia producto'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posNameColumn',
                label: t('posNameColumn', 'Posición columna nombre'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: createType
            },
            {
                name: 'posShortDescriptionColumn',
                label: t('posShortDescriptionColumn', 'Posición columna descripción corta'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posDescriptionColumn',
                label: t('posDescriptionColumn', 'Posición columna descripción'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posDescriptionDocuments',
                label: t('posDescriptionDocuments', 'Posición columna descripción doc.'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posShortDescriptionDocuments',
                label: t('posShortDescriptionDocuments', 'Posición columna descripción corta doc.'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posSupplierRefColumn',
                label: t('posSupplierRefColumn', 'Posición columna referencia proveedor'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posCodeBarColumn',
                label: t('posCodeBarColumn', 'Posición columna código de barras'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posCodeBarCustomColumn',
                label: t('posCodeBarCustomColumn', 'Posición columna código de barras personalizado'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posCategoryColumn',
                label: t('posCategoryColumn', 'Posición columna categorías'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posBrandColumn',
                label: t('posBrandColumn', 'Posición columna marca'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posImageColumn',
                label: t('posImageColumn', 'Posición columna imagen'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posNotes',
                label: t('posNotes', 'Posición notas'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posWeight',
                label: t('posWeight', 'Posición peso'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posSecondaryReference',
                label: t('posSecondaryReference', 'Posición referencia secundaria'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posCostPrice',
                label: t('posCostPrice', 'Posición precio de coste'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
        )
        if(pricesRules){
            for (const element of pricesRules.arrSorted) {
                csvFields.push({
                    name: 'SpecificPriceRuleId-'+element.id,
                    label: t('posSpecificPriceRule', 'Posición Tarifa') + ' ' + element.name,
                    component: 'InputWithLabelMarginIntegerStateFull',
                })
            }
        }
        if(features){
            for (const FeatureId in features) {
                const feature = features[FeatureId]
                csvFields.push({
                    name: 'FeatureId-' + feature.id,
                    label: t('posFeature', 'Posición Característica') + ' ' + feature.name,
                    component: 'InputWithLabelMarginIntegerStateFull',
                })
            }
        }
        if (haveModuleBuyed(['advanced_import'], getModulesBuyed())) {
            csvFields.push(
                {
                    name: 'posVariantColumn',
                    label: t('posVariantColumn', 'Posición columna variante'),
                    component: 'InputWithLabelMarginIntegerStateFull',
                },
                {
                    name: 'posVariantColumnCategory',
                    label: t('posVariantColumnCategory', 'Posición columna categoria variante'),
                    component: 'InputWithLabelMarginIntegerStateFull',
                },
                {
                    name: 'posParentReference',
                    label: t('posParentReference', 'Posición referencia padre'),
                    component: 'InputWithLabelMarginIntegerStateFull',
                },
                {
                    name: 'haveImagesIncrustrated',
                    label: t('imagesIncrustrated', 'Imagenes incrustradas'),
                    component: 'Checkbox',
                },
            )
        }
    }


    const fields: FormRender<any> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'importProductType',
                    component: 'CustomSelect',
                    items: dictImportTypes,
                    label: t('importType', 'Tipo de importación'),
                    value: importProductType,
                    onChange: handleImportProductType
                },
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                    defaultValue: getLang(),
                    required: true
                },
                {
                    name: 'GroupVatId',
                    label: t('groupVat', 'Grupo de impuestos'),
                    component: 'CustomSelectWithSearchBar',
                    items: groupVats || {},
                    required: true
                },
                {
                    name: 'filePath',
                    component: 'FilesInput',
                    label: t('uploadExcelOrCSV', 'Subir excel o CSV'),
                    acceptFileConditions: {
                        'text': ['.csv', '.xlsx']
                    },
                    onChange: setFile,
                    className: 'mt-30',
                    maxSize: 50000000
                },
                ...csvFields,
            ]
        },
    ]
    return { fields, createType }
}

const modalImportProductsDispatch = {
    setGroupVats: (payload: GroupVatPayload) => ({ type: 'CHANGE_GROUPVAT', payload }),
    setFeatures: (payload: FeaturesPayload) => ({ type: 'CHANGE_FEATURES', payload }),
}

type ReduxModalImportProducts = ConnectedProps<typeof modalImportProductsConnect>
const mapModalImportProductsConnector = (state: AllReduxPayloads) => ({ groupVats: state.groupVats, features: state.features })
const modalImportProductsConnect = connect(mapModalImportProductsConnector, modalImportProductsDispatch)

const modalImportProductsConnectLoading = withLoading(ModalImportProducts, [{ fetchUrl: '/api/app/config/vat/getAllGroupsVat', propName: 'groupVats', setFunctionName: 'setGroupVats' }, { fetchUrl: '/api/app/product/feature/getAllFeaturesComplete', propName: 'features', setFunctionName: 'setFeatures' }])

const ModalImportProductsConnect = modalImportProductsConnect(modalImportProductsConnectLoading)

export default ModalImportProductsConnect